import React from 'react';
import { motion } from 'framer-motion';
import CountingNumber from '../CountingNumber';
import { getClassificationForElo, getClassificationColor } from '../../utils/classificationUtils';
import { EloData } from '../../types/api.types';

interface PlayerEloCardProps {
  playerData: EloData;
  isLikelyFemale: boolean;
  currentLang: 'en' | 'fr' | 'de' | 'it';
  t: {
    currentEloRating: string;
    projected: string;
  };
}

/**
 * PlayerEloCard Component
 * Displays current ELO rating with classification badge and projected ELO change
 */
const PlayerEloCard: React.FC<PlayerEloCardProps> = ({
  playerData,
  isLikelyFemale,
  currentLang,
  t
}) => {
  const eloDifference = playerData.final_elo && playerData.official_elo
    ? playerData.final_elo - playerData.official_elo
    : 0;

  const currentClass = getClassificationForElo(playerData.current_elo, isLikelyFemale);
  const colors = getClassificationColor(currentClass);

  return (
    <motion.div
      className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 shadow-lg transition-colors duration-300"
      initial={{ opacity: 0, scale: 0.9, y: 0 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2, type: "spring", stiffness: 100 }}>

      {/* Classification Badge - Top Right */}
      <div className="absolute top-6 right-6">
        <motion.div
          className="px-4 py-2 rounded-xl text-lg font-bold shadow-md"
          style={{
            backgroundColor: colors.bg,
            color: colors.text
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}>
          {currentClass}
        </motion.div>
      </div>

      {/* ELO RATING Label */}
      <motion.div
        className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mb-2 transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.3 }}>
        {t.currentEloRating}
      </motion.div>

      {/* Current ELO - Large Display */}
      <motion.div
        className="flex items-center gap-3 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.4 }}>
        <div className="text-6xl md:text-7xl font-bold transition-colors duration-300" style={{ color: colors.bg }}>
          <CountingNumber target={playerData.current_elo} duration={2} delay={400} decimals={0} />
        </div>
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}>
          <svg className="w-6 h-6 md:w-8 md:h-8 text-gray-400 dark:text-gray-500 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </motion.div>
      </motion.div>

      {/* Projected ELO Change */}
      {eloDifference !== 0 && (
        <motion.div
          className={`rounded-xl p-4 transition-colors duration-300 ${
            eloDifference >= 0
              ? 'bg-emerald-50 dark:bg-emerald-950/30'
              : 'bg-rose-50 dark:bg-rose-950/30'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider mb-1 transition-colors duration-300">{t.projected}</div>
              <div className={`text-2xl md:text-3xl font-bold transition-colors duration-300 ${
                eloDifference >= 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-rose-600 dark:text-rose-500'
              }`}>
                <CountingNumber target={playerData.final_elo || playerData.current_elo} duration={2} delay={700} decimals={2} />
              </div>
            </div>
            <motion.div
              className={`px-3 py-2 md:px-4 md:py-2 rounded-lg font-bold flex items-center gap-1 ${
                eloDifference >= 0 ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-rose-500 dark:bg-rose-600'
              } text-white transition-colors duration-300`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.9 }}>
              {eloDifference >= 0 ? '↑' : '↓'} <CountingNumber target={Math.abs(eloDifference)} duration={2} delay={900} decimals={0} prefix="+" />
            </motion.div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PlayerEloCard;
